/**
 * "Update available": a running tab learns that a newer build was deployed and offers a reload — it never reloads
 * by itself. The build's identity is `__BUILD_INFO__.date` (unique per build); `/version.json` carries the deployed
 * build's. A different date, or a page chunk that can't be fetched, marks the tab stale. Stale is latched and stops
 * the polling. The only reload is the person's click (`reloadForUpdate`).
 */
import { useEffect, useSyncExternalStore } from 'react';

/** The deployed build's date from a `/version.json` body; anything else (HTML, `{}`, a number) is null. */
export function parseVersion(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const date = (body as { date?: unknown }).date;
  return typeof date === 'string' ? date : null;
}

export function isStale(local: string, remote: string | null): boolean {
  return remote !== null && remote !== local;
}

type Timer = ReturnType<typeof setTimeout>;
export interface UpdateWatchDeps {
  fetch: (url: string, init: { signal: AbortSignal; cache: RequestCache }) => Promise<{ ok: boolean; json(): Promise<unknown> }>;
  now: () => number;
  setTimeout: (fn: () => void, ms: number) => Timer;
  clearTimeout: (t: Timer) => void;
  isOnline: () => boolean;
  currentDate: string;
  intervalMs?: number;
  minGapMs?: number;
  timeoutMs?: number;
}

export interface UpdateWatch {
  subscribe(fn: () => void): () => void;
  get(): boolean;
  /** One check now (skipped while one is in flight, within the minimum gap, or once stale). */
  check(): Promise<void>;
  /** Mark the tab stale (a page chunk failed). A no-op while the browser says it is offline. */
  markStale(): void;
  /** Start the periodic timer (the first check comes after `firstMs`). */
  start(firstMs?: number): void;
  dispose(): void;
}

export function createUpdateWatch(deps: UpdateWatchDeps): UpdateWatch {
  const { intervalMs = 300_000, minGapMs = 30_000, timeoutMs = 10_000 } = deps;
  const listeners = new Set<() => void>();
  let stale = false;
  let inFlight = false;
  let last = -Infinity;
  let timer: Timer | null = null;
  let disposed = false;

  const stopTimer = () => {
    if (timer !== null) deps.clearTimeout(timer);
    timer = null;
  };
  const setStale = () => {
    if (stale) return;
    stale = true;
    stopTimer();
    listeners.forEach((l) => l());
  };
  const schedule = (ms: number) => {
    stopTimer();
    if (stale || disposed) return;
    timer = deps.setTimeout(() => {
      timer = null;
      void check().finally(() => schedule(intervalMs));
    }, ms);
  };

  async function check(): Promise<void> {
    if (stale || disposed || inFlight) return;
    const t = deps.now();
    if (t - last < minGapMs) return;
    inFlight = true;
    last = t;
    const ctrl = new AbortController();
    const abort = deps.setTimeout(() => ctrl.abort(), timeoutMs);
    try {
      const res = await deps.fetch('/version.json', { signal: ctrl.signal, cache: 'no-store' });
      if (!res.ok) return;
      let body: unknown;
      try {
        body = await res.json();
      } catch {
        return; // not JSON (an HTML fallback page): ignored
      }
      if (isStale(deps.currentDate, parseVersion(body))) setStale();
    } catch {
      // offline, timed out, refused: ignored — the next check tries again
    } finally {
      deps.clearTimeout(abort);
      inFlight = false;
    }
  }

  return {
    subscribe(fn) {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    get: () => stale,
    check,
    markStale() {
      if (!deps.isOnline()) return;
      setStale();
    },
    start(firstMs = 1_000) {
      if (timer === null && !stale && !disposed) schedule(firstMs);
    },
    dispose() {
      disposed = true;
      stopTimer();
      listeners.clear();
    },
  };
}

// ---- DOM wiring: one watch per tab ----
let watch: UpdateWatch | null = null;
let started = false;

function getWatch(): UpdateWatch | null {
  if (typeof window === 'undefined') return null;
  if (!watch) {
    watch = createUpdateWatch({
      fetch: (url, init) => window.fetch(url, init),
      now: () => Date.now(),
      setTimeout: (fn, ms) => window.setTimeout(fn, ms) as unknown as Timer,
      clearTimeout: (t) => window.clearTimeout(t as unknown as number),
      isOnline: () => navigator.onLine !== false,
      currentDate: __BUILD_INFO__.date,
    });
  }
  return watch;
}

/** Idempotent (StrictMode mounts twice): one timer, one listener of each kind. */
export function startUpdateWatch(): void {
  const w = getWatch();
  if (!w || started) return;
  started = true;
  w.start(1_000);
  window.addEventListener('focus', () => void w.check());
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') void w.check(); });
  // a chunk of the old build is gone: a new one was deployed. Never preventDefault — Vite then resolves the import
  // with undefined and the route's fallback card would become the generic crash card.
  window.addEventListener('vite:preloadError', () => w.markStale());
}

/** For the route boundary: a page chunk failed to load while online. */
export function markStale(): void {
  getWatch()?.markStale();
}

const noop = () => () => {};
export function useUpdateAvailable(): boolean {
  const w = getWatch();
  useEffect(() => startUpdateWatch(), []);
  return useSyncExternalStore(w ? w.subscribe : noop, w ? w.get : () => false, () => false);
}

/** The one reload this feature makes, and only on the person's click. */
export function reloadForUpdate(): void {
  window.location.reload();
}
